<template>
  <div class="upsert-subscription">
    <div class="page-header">
      <a-button type="text" @click="goBack">
        <template #icon>
          <ArrowLeftOutlined />
        </template>
        뒤로가기
      </a-button>
      <h2>{{ isEditMode ? "구독 수정" : "구독 추가" }}</h2>
    </div>

    <a-spin :spinning="isLoading">
      <a-form
        ref="formRef"
        :model="formState"
        :rules="formRules"
        layout="vertical"
        class="subscription-form"
      >
        <!-- 구독자 정보 -->
        <a-card title="구독자 정보" class="form-section">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="거래처" name="companyId" required>
                <a-input-group compact>
                  <a-input
                    :value="selectedCompany?.name || ''"
                    placeholder="거래처를 선택하세요"
                    readonly
                    :style="{
                      width: isEditMode ? '100%' : 'calc(100% - 100px)',
                    }"
                  />
                  <a-button
                    v-if="!isEditMode"
                    type="primary"
                    @click="companySearchVisible = true"
                  >
                    거래처 검색
                  </a-button>
                </a-input-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="사업자번호">
                <a-input
                  :value="formatBusinessNumber(selectedCompany?.cNo)"
                  disabled
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>

        <!-- 구독 상품 정보 -->
        <a-card title="구독 상품 정보" class="form-section">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="상품 선택" name="packageId" required>
                <a-select
                  v-model:value="formState.packageId"
                  placeholder="상품을 선택하세요"
                  :loading="packagesLoading"
                  :options="packageOptions"
                  show-search
                  :filter-option="filterOption"
                  @change="handlePackageSelect"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-item label="설치비(VAT포함)" name="finalInstallationFee">
                <a-input
                  :value="formatPrice(formState.finalInstallationFee)"
                  disabled
                  style="width: 100%"
                />
                <div
                  v-if="formState.finalInstallationFee > 0"
                  class="price-info"
                >
                  VAT제외:
                  {{
                    formatPrice(
                      Math.round(formState.finalInstallationFee / 1.1)
                    )
                  }}
                </div>
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="기준 사용료(VAT포함)" name="originalUsageFee">
                <a-input
                  :value="formatPrice(formState.originalUsageFee)"
                  disabled
                  style="width: 100%"
                />
                <div v-if="formState.originalUsageFee > 0" class="price-info">
                  VAT제외:
                  {{
                    formatPrice(Math.round(formState.originalUsageFee / 1.1))
                  }}
                </div>
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="최종 사용료(VAT포함)" name="finalUsageFee">
                <a-input
                  :value="formatPrice(formState.finalUsageFee)"
                  disabled
                  style="width: 100%"
                />
                <div v-if="formState.finalUsageFee > 0" class="price-info">
                  VAT제외:
                  {{ formatPrice(Math.round(formState.finalUsageFee / 1.1)) }}
                </div>
                <div
                  v-if="formState.originalUsageFee > formState.finalUsageFee"
                  class="discount-info-text"
                >
                  할인: -{{
                    formatPrice(
                      formState.originalUsageFee - formState.finalUsageFee
                    )
                  }}
                </div>
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 추가 할인 설정 (선택 사항) -->
          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-item label="추가 할인 유형" name="discountType">
                <a-select
                  v-model:value="formState.discountType"
                  placeholder="추가 할인 유형 선택"
                  allowClear
                  :disabled="!formState.packageId"
                  @change="handleDiscountTypeChange"
                >
                  <a-select-option value="PERCENTAGE"
                    >정률 할인 (%)</a-select-option
                  >
                  <a-select-option value="FIXED"
                    >정액 할인 (원)</a-select-option
                  >
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="할인 값" name="discountValue">
                <a-input-number
                  v-model:value="formState.discountValue"
                  :formatter="discountFormatter"
                  :parser="discountParser"
                  style="width: 100%"
                  :min="0"
                  :max="
                    formState.discountType === 'PERCENTAGE'
                      ? 100
                      : formState.originalUsageFee
                  "
                  :disabled="!formState.packageId || !formState.discountType"
                  @change="applyDiscount"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="할인 금액">
                <div class="discount-summary">
                  <span v-if="discountAmount > 0" class="discount-amount">
                    -{{ formatPrice(discountAmount) }}
                  </span>
                  <span v-else class="no-discount">할인 없음</span>
                </div>
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>

        <!-- 결제 정보 -->
        <a-card title="결제 정보" class="form-section">
          <a-row :gutter="24">
            <a-col :span="6">
              <a-form-item label="결제 주기" name="paymentCycle" required>
                <a-input
                  :value="getPaymentCycleLabel(formState.paymentCycle)"
                  disabled
                  style="width: 100%"
                />
                <div class="price-info">상품 선택 시 자동 설정됩니다</div>
              </a-form-item>
            </a-col>
            <!-- 결제일 (1회성이 아닌 경우에만 표시, 메모 용도) -->
            <a-col v-if="!isOncePayment" :span="6">
              <a-form-item label="결제일 (메모)">
                <a-select
                  v-model:value="formState.paymentDay"
                  placeholder="결제일 선택"
                >
                  <a-select-option v-for="day in 28" :key="day" :value="day">
                    매월 {{ day }}일
                  </a-select-option>
                </a-select>
                <div class="price-info">결제 회차 관리용 메모입니다</div>
              </a-form-item>
            </a-col>
            <a-col :span="isOncePayment ? 18 : 12">
              <a-form-item label="결제 카드">
                <div class="card-selector">
                  <template v-if="selectedCard">
                    <div
                      class="selected-card-info"
                      :class="{ 'is-expired': isSelectedCardExpired }"
                    >
                      <span class="card-number">{{
                        selectedCard.cardNumber || "****-****-****-****"
                      }}</span>
                      <span
                        v-if="selectedCard.cardCompany"
                        class="card-company"
                        >{{ selectedCard.cardCompany }}</span
                      >
                      <a-tag
                        v-if="selectedCardExpiryStatus"
                        :color="selectedCardExpiryStatus.color"
                        size="small"
                      >
                        {{
                          selectedCardExpiryStatus.status === "expired"
                            ? "만료됨"
                            : selectedCardExpiryStatus.text
                        }}
                      </a-tag>
                    </div>
                    <a-button
                      :type="isSelectedCardExpired ? 'primary' : 'link'"
                      :danger="isSelectedCardExpired"
                      size="small"
                      @click="openCardModal"
                    >
                      {{ isSelectedCardExpired ? "카드 변경 필요" : "변경" }}
                    </a-button>
                  </template>
                  <template v-else>
                    <a-button
                      type="primary"
                      size="small"
                      @click="openCardModal"
                      :disabled="!formState.companyId"
                    >
                      카드 등록
                    </a-button>
                  </template>
                </div>
                <!-- 카드 만료 경고 Alert -->
                <a-alert
                  v-if="isSelectedCardExpired"
                  type="error"
                  show-icon
                  style="margin-top: 8px"
                >
                  <template #icon><ExclamationCircleOutlined /></template>
                  <template #message>
                    <span
                      >등록된 카드가 만료되었습니다. 결제를 위해 새 카드를
                      등록해주세요.</span
                    >
                  </template>
                </a-alert>
                <!-- 카드 만료 임박 경고 Alert -->
                <a-alert
                  v-else-if="
                    selectedCardExpiryStatus?.status === 'expiring_soon'
                  "
                  type="warning"
                  show-icon
                  style="margin-top: 8px"
                >
                  <template #icon><WarningOutlined /></template>
                  <template #message>
                    <span
                      >카드가 {{ selectedCardExpiryStatus.text }} 예정입니다.
                      미리 새 카드를 등록해주세요.</span
                    >
                  </template>
                </a-alert>
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>

        <!-- 계약 정보 -->
        <a-card title="계약 정보" class="form-section">
          <a-row :gutter="24">
            <!-- 1회성 결제: 계약일만 표시 -->
            <template v-if="isOncePayment">
              <a-col :span="8">
                <a-form-item label="계약일" name="contractDate" required>
                  <a-date-picker
                    v-model:value="formState.contractDate"
                    format="YYYY-MM-DD"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
            </template>
            <!-- 정기 결제: 시작일, 종료일 표시 -->
            <template v-else>
              <a-col :span="8">
                <a-form-item label="서비스 시작일" name="startDate">
                  <a-date-picker
                    v-model:value="formState.startDate"
                    format="YYYY-MM-DD"
                    style="width: 100%"
                    placeholder="시작일 선택"
                  />
                  <div class="price-info">
                    구독 서비스가 시작되는 날짜입니다
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="8">
                <a-form-item label="계약 종료일" name="endDate">
                  <a-date-picker
                    v-model:value="formState.endDate"
                    format="YYYY-MM-DD"
                    style="width: 100%"
                    placeholder="종료일 선택 (선택사항)"
                  />
                </a-form-item>
              </a-col>
            </template>
            <a-col :span="isOncePayment ? 16 : 8">
              <a-form-item label="상태" name="status">
                <a-select
                  v-model:value="formState.status"
                  placeholder="상태 선택"
                >
                  <a-select-option
                    v-for="option in availableStatusOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </a-select-option>
                </a-select>
                <div v-if="statusChangeWarning" class="status-warning">
                  {{ statusChangeWarning }}
                </div>
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 무료체험 설정 -->
          <a-row :gutter="24">
            <a-col :span="6">
              <a-form-item label="무료체험 제공">
                <a-switch
                  v-model:checked="formState.freeTrialEnabled"
                  checked-children="제공"
                  un-checked-children="미제공"
                />
              </a-form-item>
            </a-col>
            <a-col v-if="formState.freeTrialEnabled" :span="6">
              <a-form-item label="무료체험 기간(일)" name="freeTrialDays">
                <a-input-number
                  v-model:value="formState.freeTrialDays"
                  :min="1"
                  :max="365"
                  placeholder="일수"
                  style="width: 100%"
                />
              </a-form-item>
            </a-col>
            <a-col v-if="formState.freeTrialEnabled" :span="12">
              <a-form-item label="무료체험 제공 사유" name="freeTrialReason">
                <a-input
                  v-model:value="formState.freeTrialReason"
                  placeholder="예: 신규 고객 프로모션"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <div v-if="formState.freeTrialEnabled" class="free-trial-info">
            <a-alert type="info" show-icon>
              <template #message>
                무료체험은 구독 상태가 '활성'이 되는 시점부터
                {{ formState.freeTrialDays || 0 }}일간 적용됩니다. 무료체험 기간
                동안 결제가 발생하지 않습니다.
              </template>
            </a-alert>
          </div>

          <a-form-item label="메모" name="memo">
            <a-textarea
              v-model:value="formState.memo"
              placeholder="메모를 입력하세요"
              :rows="3"
            />
          </a-form-item>
        </a-card>

        <!-- 하단 버튼 -->
        <div class="form-actions">
          <a-button size="large" @click="goBack">취소</a-button>
          <a-button
            type="primary"
            size="large"
            :loading="isSubmitting"
            @click="handleSubmit"
          >
            {{ isEditMode ? "수정" : "등록" }}
          </a-button>
        </div>
      </a-form>
    </a-spin>

    <!-- 거래처 검색 모달 -->
    <CompanySearchModal
      v-model:open="companySearchVisible"
      @select="handleCompanySelect"
    />

    <!-- 카드 관리 모달 -->
    <CardManagementModal
      v-model:open="cardModalVisible"
      :company-id="formState.companyId"
      :current-card-id="formState.cardId"
      :company-business-number="selectedCompany?.cNo || ''"
      @select="handleCardSelect"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import {
  ArrowLeftOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons-vue";
import dayjs from "dayjs";
import {
  resellerSubscriptionApi,
  resellerCompanyCardApi,
  subscriptionPackageApi,
} from "./api";
import {
  PaymentCycleLabel,
  ResellerSubscriptionStatus,
  ResellerSubscriptionStatusLabel,
} from "./enums";
import CompanySearchModal from "./components/CompanySearchModal.vue";
import CardManagementModal from "./components/CardManagementModal.vue";
import { isCardExpired, getCardExpiryStatus } from "./utils/cardExpiry";

export default defineComponent({
  name: "UpsertSubscriptionV2",
  components: {
    ArrowLeftOutlined,
    WarningOutlined,
    ExclamationCircleOutlined,
    CompanySearchModal,
    CardManagementModal,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const isEditMode = computed(() => !!route.params.id);
    const subscriptionId = computed(() => route.params.id);

    const isLoading = ref(false);
    const isSubmitting = ref(false);

    const formRef = ref(null);
    const companySearchVisible = ref(false);
    const cardModalVisible = ref(false);

    const selectedCompany = ref(null);
    const selectedCard = ref(null);

    // 상품 목록
    const packages = ref([]);
    const packagesLoading = ref(false);

    // 상품 드롭다운 옵션
    const packageOptions = computed(() => {
      return packages.value.map((pkg) => ({
        value: pkg._id,
        label: `${pkg.name} - ${formatPrice(
          pkg.pricing?.finalUsageFee || 0
        )}/월`,
        data: pkg,
      }));
    });

    // 결제 주기 라벨 반환
    const getPaymentCycleLabel = (cycle) => {
      return PaymentCycleLabel[cycle] || cycle || "상품을 선택하세요";
    };

    const formState = ref({
      companyId: "",
      packageId: "",
      packageName: "",
      originalInstallationFee: 0,
      originalUsageFee: 0,
      discountType: undefined,
      discountValue: undefined,
      finalInstallationFee: 0,
      finalUsageFee: 0,
      paymentCycle: "MONTHLY",
      paymentMonth: 1,
      paymentDay: 5,
      startDate: null,
      endDate: null,
      contractDate: null,
      cardId: undefined,
      status: "CONTRACT",
      freeTrialEnabled: false,
      freeTrialDays: undefined,
      freeTrialReason: undefined,
      memo: "",
    });

    // 1회성 결제인지 여부
    const isOncePayment = computed(
      () => formState.value.paymentCycle === "ONCE"
    );

    // 원래 상태 (수정 모드에서 불러온 상태)
    const originalStatus = ref("CONTRACT");

    // 상태 라벨 맵
    const STATUS_LABELS = ResellerSubscriptionStatusLabel;

    // 현재 상태에서 선택 가능한 상태 옵션
    const availableStatusOptions = computed(() => {
      const currentStatus = originalStatus.value;
      const allStatuses = [
        "CONTRACT",
        "SETUP",
        "INSPECTION",
        "ACTIVE",
        "SUSPENDED",
        "TERMINATED",
      ];

      return allStatuses.map((status) => ({
        value: status,
        label:
          status === currentStatus
            ? `${STATUS_LABELS[status]} (현재)`
            : STATUS_LABELS[status],
      }));
    });

    // 상태 변경 경고 메시지
    const statusChangeWarning = computed(() => {
      const currentStatus = originalStatus.value;
      const newStatus = formState.value.status;

      if (currentStatus === newStatus) return "";

      if (newStatus === "TERMINATED") {
        return "종료 상태로 변경하면 되돌릴 수 없습니다.";
      }
      if (newStatus === "SUSPENDED") {
        return "중단 상태에서는 재활성화가 불가능합니다. 재구독이 필요한 경우 새 구독을 생성해주세요.";
      }
      return "";
    });

    // 결제 주기 변경 시 필드 초기화
    const applyPaymentCycleChange = () => {
      if (formState.value.paymentCycle === "ONCE") {
        formState.value.startDate = null;
        formState.value.endDate = null;
        formState.value.paymentDay = 5;
      } else {
        formState.value.contractDate = null;
      }
    };

    // 무료체험 활성화 여부
    const isFreeTrialEnabled = computed(
      () => formState.value.freeTrialEnabled === true
    );

    // 동적 validation rules
    const formRules = computed(() => ({
      companyId: [{ required: true, message: "거래처를 선택해주세요" }],
      packageId: [{ required: true, message: "상품을 선택해주세요" }],
      originalInstallationFee: [
        { required: true, message: "설치비를 입력해주세요" },
      ],
      originalUsageFee: [{ required: true, message: "정가를 입력해주세요" }],
      finalUsageFee: [{ required: true, message: "결제금액을 입력해주세요" }],
      paymentCycle: [{ required: true, message: "결제 주기를 선택해주세요" }],
      contractDate: [
        { required: isOncePayment.value, message: "계약일을 선택해주세요" },
      ],
      freeTrialDays: [
        {
          required: isFreeTrialEnabled.value,
          message: "무료체험 일수를 입력해주세요",
        },
      ],
      freeTrialReason: [
        {
          required: isFreeTrialEnabled.value,
          message: "무료체험 사유를 입력해주세요",
        },
      ],
    }));

    // 할인 금액 계산
    const discountAmount = computed(() => {
      if (!formState.value.discountType || !formState.value.discountValue)
        return 0;

      if (formState.value.discountType === "PERCENTAGE") {
        return Math.round(
          formState.value.originalUsageFee *
            (formState.value.discountValue / 100)
        );
      } else {
        return formState.value.discountValue;
      }
    });

    // 상품 목록 조회
    const fetchPackages = async () => {
      packagesLoading.value = true;
      try {
        const response = await subscriptionPackageApi.getAll({
          isActive: true,
        });
        packages.value = response.data || [];
      } catch (error) {
        console.error("상품 목록 조회 실패:", error);
        packages.value = [];
      } finally {
        packagesLoading.value = false;
      }
    };

    // 회사 카드 목록에서 선택된 카드 조회
    const fetchSelectedCard = async (companyId, cardId) => {
      if (!cardId) {
        selectedCard.value = null;
        return;
      }

      try {
        const response = await resellerCompanyCardApi.getByCompany(companyId);
        selectedCard.value =
          response.data.find((card) => card._id === cardId) || null;
      } catch (error) {
        console.error("카드 정보 조회 실패:", error);
        selectedCard.value = null;
      }
    };

    const fetchSubscription = async () => {
      if (!isEditMode.value) return;

      try {
        isLoading.value = true;
        const response = await resellerSubscriptionApi.getDetail(
          subscriptionId.value
        );
        const data = response.data;

        // 원래 상태 저장
        originalStatus.value = data.status;

        formState.value = {
          companyId: data.companyId,
          packageId: data.packageId,
          packageName: data.packageName,
          originalInstallationFee: data.originalInstallationFee,
          originalUsageFee: data.originalUsageFee,
          discountType: data.discountType,
          discountValue: data.discountValue,
          finalInstallationFee: data.finalInstallationFee,
          finalUsageFee: data.finalUsageFee,
          paymentCycle: data.paymentCycle,
          paymentMonth: data.paymentMonth || 1,
          paymentDay: data.paymentDay || 5,
          startDate: data.startDate ? dayjs(data.startDate) : null,
          endDate: data.endDate ? dayjs(data.endDate) : null,
          contractDate: data.contractDate ? dayjs(data.contractDate) : null,
          cardId: data.cardId,
          status: data.status,
          freeTrialEnabled: data.freeTrialEnabled || false,
          freeTrialDays: data.freeTrialDays,
          freeTrialReason: data.freeTrialReason,
          memo: data.notes || "",
        };

        // 거래처 정보 설정
        if (data.companyId) {
          selectedCompany.value = {
            _id: data.companyId,
            name: data.companyName || "",
            cNo: data.businessNumber || "",
          };
        }

        // 카드 정보 조회
        if (data.cardId) {
          await fetchSelectedCard(data.companyId, data.cardId);
        }
      } catch (error) {
        console.error("구독 조회 실패:", error);
        message.error("구독 정보를 불러오는데 실패했습니다.");
      } finally {
        isLoading.value = false;
      }
    };

    const handleCompanySelect = (company) => {
      selectedCompany.value = company;
      formState.value.companyId = company._id;
      // 회사가 바뀌면 카드 초기화
      formState.value.cardId = undefined;
      selectedCard.value = null;
    };

    // 상품 패키지 선택 핸들러
    const handlePackageSelect = (packageId) => {
      const selectedPackage = packages.value.find(
        (pkg) => pkg._id === packageId
      );
      if (selectedPackage) {
        formState.value.packageId = packageId;
        formState.value.packageName = selectedPackage.name;
        formState.value.originalInstallationFee =
          selectedPackage.pricing?.baseInstallationFee || 0;
        formState.value.originalUsageFee =
          selectedPackage.pricing?.baseUsageFee || 0;
        formState.value.finalInstallationFee =
          selectedPackage.pricing?.finalInstallationFee || 0;
        formState.value.finalUsageFee =
          selectedPackage.pricing?.finalUsageFee || 0;

        // 할인 정보 초기화
        formState.value.discountType = undefined;
        formState.value.discountValue = undefined;

        // billingType에서 paymentCycle 자동 설정
        const billingTypeMap = {
          monthly: "MONTHLY",
          yearly: "YEARLY",
          once: "ONCE",
        };
        formState.value.paymentCycle =
          billingTypeMap[selectedPackage.billingType] || "MONTHLY";
        applyPaymentCycleChange();
      }
    };

    const handleDiscountTypeChange = () => {
      formState.value.discountValue = undefined;
      formState.value.finalUsageFee = formState.value.originalUsageFee;
    };

    const applyDiscount = () => {
      if (!formState.value.discountType || !formState.value.discountValue) {
        formState.value.finalUsageFee = formState.value.originalUsageFee;
        return;
      }

      let discountedPrice = formState.value.originalUsageFee;

      if (formState.value.discountType === "PERCENTAGE") {
        discountedPrice = Math.round(
          formState.value.originalUsageFee *
            (1 - formState.value.discountValue / 100)
        );
      } else {
        discountedPrice =
          formState.value.originalUsageFee - formState.value.discountValue;
      }

      formState.value.finalUsageFee = Math.max(0, discountedPrice);
    };

    const openCardModal = () => {
      if (!formState.value.companyId) {
        message.warning("먼저 거래처를 선택해주세요.");
        return;
      }
      cardModalVisible.value = true;
    };

    const handleCardSelect = (cardId) => {
      formState.value.cardId = cardId;
      fetchSelectedCard(formState.value.companyId, cardId);
      cardModalVisible.value = false;
    };

    const formatPrice = (value) => {
      if (!value && value !== 0) return "-";
      return value.toLocaleString() + "원";
    };

    const formatBusinessNumber = (num) => {
      if (!num || num.length !== 10) return num || "";
      return `${num.slice(0, 3)}-${num.slice(3, 5)}-${num.slice(5)}`;
    };

    // 할인 포맷터
    const discountFormatter = (value) => {
      if (formState.value.discountType === "PERCENTAGE") {
        return `${value}%`;
      }
      return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const discountParser = (value) => {
      return Number(value.replace(/[%,]/g, ""));
    };

    // 필터 옵션
    const filterOption = (input, option) => {
      return option.label.toLowerCase().includes(input.toLowerCase());
    };

    const handleSubmit = async () => {
      try {
        await formRef.value?.validate();

        if (!formState.value.packageId) {
          message.error("상품을 선택해주세요.");
          return;
        }

        // 활성화 상태일 때 카드 필수 검증
        if (formState.value.status === "ACTIVE" && !formState.value.cardId) {
          message.error(
            "활성화 상태로 저장하려면 결제카드를 먼저 등록해주세요."
          );
          return;
        }

        isSubmitting.value = true;

        const isOnce = formState.value.paymentCycle === "ONCE";

        if (isEditMode.value) {
          // 수정 모드
          const updatePayload = {
            originalInstallationFee: formState.value.originalInstallationFee,
            originalUsageFee: formState.value.originalUsageFee,
            discountType: formState.value.discountType,
            discountValue: formState.value.discountValue,
            finalInstallationFee: formState.value.finalInstallationFee,
            finalUsageFee: formState.value.finalUsageFee,
            paymentCycle: formState.value.paymentCycle,
            paymentMonth:
              formState.value.paymentCycle === "YEARLY"
                ? formState.value.paymentMonth
                : undefined,
            paymentDay: isOnce ? undefined : formState.value.paymentDay,
            startDate: isOnce
              ? undefined
              : formState.value.startDate
              ? formState.value.startDate.format("YYYY-MM-DD")
              : null,
            endDate: isOnce
              ? undefined
              : formState.value.endDate
              ? formState.value.endDate.format("YYYY-MM-DD")
              : null,
            contractDate: isOnce
              ? formState.value.contractDate
                ? formState.value.contractDate.format("YYYY-MM-DD")
                : null
              : undefined,
            cardId: formState.value.cardId,
            status: formState.value.status,
            freeTrialEnabled: formState.value.freeTrialEnabled,
            freeTrialDays: formState.value.freeTrialEnabled
              ? formState.value.freeTrialDays
              : undefined,
            freeTrialReason: formState.value.freeTrialEnabled
              ? formState.value.freeTrialReason
              : undefined,
            notes: formState.value.memo,
          };
          await resellerSubscriptionApi.update(
            subscriptionId.value,
            updatePayload
          );
          message.success("구독이 수정되었습니다.");
        } else {
          // 생성 모드
          const createPayload = {
            companyId: formState.value.companyId,
            companyName: selectedCompany.value?.name || "",
            businessNumber: selectedCompany.value?.cNo,
            packageId: formState.value.packageId,
            packageName: formState.value.packageName,
            originalInstallationFee: formState.value.originalInstallationFee,
            originalUsageFee: formState.value.originalUsageFee,
            discountType: formState.value.discountType,
            discountValue: formState.value.discountValue,
            finalInstallationFee: formState.value.finalInstallationFee,
            finalUsageFee: formState.value.finalUsageFee,
            paymentCycle: formState.value.paymentCycle,
            paymentMonth:
              formState.value.paymentCycle === "YEARLY"
                ? formState.value.paymentMonth
                : undefined,
            paymentDay: isOnce ? undefined : formState.value.paymentDay,
            startDate: isOnce
              ? undefined
              : formState.value.startDate?.format("YYYY-MM-DD"),
            endDate: isOnce
              ? undefined
              : formState.value.endDate?.format("YYYY-MM-DD"),
            contractDate: isOnce
              ? formState.value.contractDate?.format("YYYY-MM-DD")
              : undefined,
            cardId: formState.value.cardId,
            status: formState.value.status,
            freeTrialEnabled: formState.value.freeTrialEnabled,
            freeTrialDays: formState.value.freeTrialEnabled
              ? formState.value.freeTrialDays
              : undefined,
            freeTrialReason: formState.value.freeTrialEnabled
              ? formState.value.freeTrialReason
              : undefined,
            notes: formState.value.memo,
          };
          await resellerSubscriptionApi.create(createPayload);
          message.success("구독이 등록되었습니다.");
        }

        goBack();
      } catch (error) {
        console.error("구독 저장 실패:", error);
        if (error.errorFields) {
          message.error("필수 항목을 확인해주세요.");
        } else {
          message.error(
            error.response?.data?.message || "구독 저장에 실패했습니다."
          );
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const goBack = () => {
      // 수정 모드일 때는 상세 페이지로, 생성 모드일 때는 목록으로 돌아감
      if (isEditMode.value) {
        router.push({
          name: "subscriptionDetailV2",
          params: { id: subscriptionId.value },
        });
      } else {
        router.push({ name: "subscriptionManagementV2" });
      }
    };

    // 선택된 카드의 만료 상태 확인
    const selectedCardExpiryStatus = computed(() => {
      if (!selectedCard.value?.cardExpiry) return null;
      return getCardExpiryStatus(selectedCard.value.cardExpiry);
    });

    // 선택된 카드가 만료되었는지 확인
    const isSelectedCardExpired = computed(() => {
      if (!selectedCard.value?.cardExpiry) return false;
      return isCardExpired(selectedCard.value.cardExpiry);
    });

    // 쿼리 파라미터로 전달된 companyId 처리
    const initFromQueryParams = async () => {
      const queryCompanyId = route.query.companyId;
      const queryCompanyName = route.query.companyName;
      const queryBusinessNumber = route.query.businessNumber;

      if (queryCompanyId && !isEditMode.value) {
        selectedCompany.value = {
          _id: queryCompanyId,
          name: queryCompanyName || "",
          cNo: queryBusinessNumber || "",
        };
        formState.value.companyId = queryCompanyId;
      }
    };

    onMounted(async () => {
      await fetchPackages();
      if (isEditMode.value) {
        await fetchSubscription();
      } else {
        await initFromQueryParams();
      }
    });

    return {
      isEditMode,
      isLoading,
      isSubmitting,
      formRef,
      formState,
      formRules,
      companySearchVisible,
      cardModalVisible,
      selectedCompany,
      selectedCard,
      packages,
      packagesLoading,
      packageOptions,
      discountAmount,
      isOncePayment,
      getPaymentCycleLabel,
      availableStatusOptions,
      statusChangeWarning,
      handleCompanySelect,
      handlePackageSelect,
      handleDiscountTypeChange,
      applyDiscount,
      openCardModal,
      handleCardSelect,
      formatPrice,
      formatBusinessNumber,
      discountFormatter,
      discountParser,
      filterOption,
      handleSubmit,
      goBack,
      selectedCardExpiryStatus,
      isSelectedCardExpired,
      isCardExpired,
      getCardExpiryStatus,
    };
  },
});
</script>

<style scoped>
.upsert-subscription {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.subscription-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  border-radius: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 0;
}

.price-info {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.discount-summary {
  height: 32px;
  display: flex;
  align-items: center;
}

.discount-amount {
  font-size: 16px;
  font-weight: 600;
  color: #f5222d;
}

.no-discount {
  color: #999;
}

.discount-info-text {
  margin-top: 4px;
  font-size: 12px;
  color: #f5222d;
  font-weight: 500;
}

.card-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  flex-wrap: wrap;
}

.selected-card-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.selected-card-info.is-expired {
  opacity: 0.7;
}

.selected-card-info.is-expired .card-number {
  text-decoration: line-through;
  color: #ff4d4f;
}

.card-number {
  font-family: monospace;
  font-size: 14px;
}

.card-company {
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
}

.text-muted {
  color: #999;
}

.free-trial-info {
  margin-top: 16px;
  margin-bottom: 16px;
}

.status-warning {
  margin-top: 4px;
  font-size: 12px;
  color: #faad14;
  font-weight: 500;
}
</style>
